<template>
  <component
    :is="tooltipOrDiv"
    :mount-to-body="true"
    :placement="placement"
    :include-cta="isDeprecatedMop"
    :button-text="ctaText"
    :cta-icon="ctaIcon"
    :stop-propagation="isDeprecatedMop"
    @cta-clicked="emit('cta-clicked')"
  >
    <component
      :is="avatarComponent"
      :size="size"
      :org="partner"
      :icon-slot-bg-color="iconSlotBgColor"
      :is-icon="isOfflineOrVirtual"
      :is-user-main-avatar="false"
      :show-initials="true"
    >
      <template #icon>
        <FontAwesomeIcon
          :icon="['fak', icon]"
          :style="{
            height: '12px',
            width: '12px',
            color: 'currentColor',
          }"
          :class="{
            'text-white': effectiveHasOpenDataPartner || isDeprecatedMop,
            'text-info-accent':
              isVirtual && !effectiveHasOpenDataPartner && !isDeprecatedMop,
            'text-neutral-accent': !isVirtual && !effectiveHasOpenDataPartner,
          }"
        />
      </template>
    </component>
    <template #title>
      <template v-if="isDeprecatedMop">
        Managed Offline Partners are no longer maintained.
        <br /><br />
        <template v-if="hasExistingOpenDataPartner">
          {{ partnerName }} is also an Open Data Partner. Delete this older
          record to clean up your list, or keep it to preserve its historical
          reports and lists.
        </template>
        <template v-else>
          Replace this partner with an Open Data Partner to continue receiving
          maintained public insights.
        </template>
      </template>
      <template v-else>
        {{ tooltipText }}
      </template>
    </template>
  </component>
</template>

<script setup lang="ts">
import {
  BittsAvatar,
  BittsAvatarPip,
  type BittsAvatarSize,
  type BittsIcon,
  BittsTooltip,
} from '@crossbeam/bitts';

import { computed, toRefs } from 'vue';

import useHasOpenDataPartnerAccess from '@/composables/useHasOpenDataPartnerAccess';
import { usePartnersStore } from '@/stores';

const props = withDefaults(
  defineProps<{
    partnerUuid?: string;
    size?: BittsAvatarSize;
    showTooltip?: boolean;
    placement?: string;
    isConnected?: boolean;
    isDeprecatedMop?: boolean;
    hasExistingOpenDataPartner?: boolean;
    hasDeprecatedMopActions?: boolean;
  }>(),
  {
    partnerUuid: '',
    size: 'medium',
    showTooltip: false,
    placement: 'topLeft',
    isConnected: false,
    isDeprecatedMop: false,
    hasExistingOpenDataPartner: false,
    hasDeprecatedMopActions: true,
  },
);

const emit = defineEmits<{
  'cta-clicked': [];
}>();

const ctaText = computed(() => {
  if (!props.isDeprecatedMop) return '';
  if (!props.hasDeprecatedMopActions) return 'View Partner';
  return props.hasExistingOpenDataPartner
    ? 'Delete Partner'
    : 'Replace Partner';
});

const ctaIcon = computed<BittsIcon | null>(() => {
  if (!props.isDeprecatedMop) return null;
  if (!props.hasDeprecatedMopActions) return ['far', 'arrow-right'];
  return props.hasExistingOpenDataPartner
    ? ['fak', 'delete']
    : ['far', 'shuffle'];
});

const { partnerUuid, size, showTooltip, placement, isConnected } =
  toRefs(props);

const hasAddOpenDataPartner = useHasOpenDataPartnerAccess();
const effectiveHasOpenDataPartner = computed(
  () => hasAddOpenDataPartner.value && !props.isDeprecatedMop,
);

const partnersStore = usePartnersStore();
const partner = computed(() =>
  partnersStore.getPartnerOrgByUuid(partnerUuid.value),
);

const partnerName = computed(() => partner.value?.name ?? 'This partner');

const isVirtual = computed(() => partner.value?.virtual_partner);
const isOffline = computed(() => partner.value?.offline_partner);
const isOfflineOrVirtual = computed(() => isOffline.value || isVirtual.value);

const avatarComponent = computed(() =>
  isOfflineOrVirtual.value && size.value !== 'x-small'
    ? BittsAvatarPip
    : BittsAvatar,
);

const tooltipOrDiv = computed(() =>
  showTooltip.value && isOfflineOrVirtual.value ? BittsTooltip : 'div',
);

const tooltipText = computed(() => {
  if (isVirtual.value) {
    return effectiveHasOpenDataPartner.value
      ? 'Open Data Partners are sourced and maintained by Crossbeam'
      : 'Managed Offline Partner data is sourced and maintained by Crossbeam';
  }
  return 'Offline partner';
});

const icon = computed(() =>
  isVirtual.value ? 'managed-offline-partner' : 'offline-partner',
);

const iconSlotBgColor = computed(() => {
  if (props.isDeprecatedMop) return 'bg-warning-accent';
  if (effectiveHasOpenDataPartner.value) {
    if (isConnected.value) return 'bg-green-400';
    return isVirtual.value ? 'bg-info-accent' : 'bg-neutral-accent';
  }
  return isVirtual.value
    ? 'bg-gradient-to-b from-teal-50 to-white'
    : 'bg-gradient-to-b from-[#E6EAF1] to-white';
});
</script>
